import { useRef, useState, useMemo } from 'react'
import {
  Box,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Badge,
  HStack,
  Button,
  Select,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { Search, RefreshCw } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'

interface AuditLog {
  timestamp: string
  timestamp_readable?: string
  user_login?: string
  user_id?: number
  module?: string
  action?: string
  user_agent?: string
  ip_address?: string
  url?: string
  description?: string
  data?: any
}

export default function ActivityLogsPage() {
  const colors = useThemeColors()
  const searchRef = useRef<HTMLInputElement>(null)
  const tableRef = useRef<DataTableRef>(null)
  const [activeTab, setActiveTab] = useState<'today' | 'range' | 'search'>('today')
  const [totalRows, setTotalRows] = useState(0)
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  })
  const [searchFilters, setSearchFilters] = useState({
    user_id: '',
    module: '',
    action: '',
    keyword: '',
  })
  const [params, setParams] = useState({
    date: new Date().toISOString().split('T')[0],
    search: '',
  })

  const getDataSource = () => {
    switch (activeTab) {
      case 'today':
        return '/audit-trail/logs/date'
      case 'range':
        return '/audit-trail/logs/range'
      case 'search':
        return '/audit-trail/logs/search'
      default:
        return '/audit-trail/logs/date'
    }
  }

  const tableParams = useMemo(() => {
    const baseParams: Record<string, string> = {}

    switch (activeTab) {
      case 'today':
        baseParams.date = params.date
        if (params.search) baseParams.search = params.search
        break
      case 'range':
        baseParams.start_date = dateRange.startDate
        baseParams.end_date = dateRange.endDate
        if (params.search) baseParams.search = params.search
        break
      case 'search':
        if (searchFilters.user_id) baseParams.user_id = searchFilters.user_id
        if (searchFilters.module) baseParams.module = searchFilters.module
        if (searchFilters.action) baseParams.action = searchFilters.action
        if (searchFilters.keyword) baseParams.keyword = searchFilters.keyword
        if (params.search) baseParams.keyword = params.search
        break
    }

    return baseParams
  }, [activeTab, params, dateRange, searchFilters])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        timestamp: {
          name: 'Timestamp',
          withSort: true,
          maxWidth: '180px',
        },
        user_login: {
          name: 'User',
          withSort: true,
          maxWidth: '150px',
        },
        module: {
          name: 'Module',
          withSort: true,
          maxWidth: '150px',
        },
        action: {
          name: 'Action',
          withSort: true,
          maxWidth: '150px',
        },
        ip_address: {
          name: 'IP Address',
          withSort: true,
          maxWidth: '150px',
        },
        user_agent: {
          name: 'User Agent',
          withSort: false,
          maxWidth: '300px',
        },
      },
      dataSource: getDataSource(),
      primaryKey: 'timestamp',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No activity logs found',
      showActions: false,
      showBulkActions: false,
    }),
    [activeTab]
  )

  const showSubSubCallback = useMemo(
    () => (all: number) => {
      setTotalRows(all)
    },
    []
  )

  const handleTabChange = (index: number) => {
    const tabs: Array<'today' | 'range' | 'search'> = ['today', 'range', 'search']
    setActiveTab(tabs[index])
    setParams((prev) => ({ ...prev, search: '' }))
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setParams((prev) => ({ ...prev, search: query }))
    tableRef.current?.clearPage()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }))
  }

  const handleFilterChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyRange = () => {
    tableRef.current?.clearPage()
    tableRef.current?.reload()
  }

  const handleApplySearch = () => {
    tableRef.current?.clearPage()
    tableRef.current?.reload()
  }

  const handleRefresh = () => {
    tableRef.current?.reload()
  }

  const getModuleColor = (module?: string) => {
    const colorMap: Record<string, string> = {
      AUTHENTICATION: 'blue',
      USER_MANAGEMENT: 'cyan',
      CONTENT_MANAGEMENT: 'green',
      INVENTORY_MANAGEMENT: 'teal',
      AUDIT_MANAGEMENT: 'yellow',
      STORE_MANAGEMENT: 'gray',
      SYSTEM_SETTINGS: 'purple',
      DASHBOARD: 'pink',
      FILE_MANAGEMENT: 'orange',
    }
    return colorMap[module || ''] || 'gray'
  }

  const getActionColor = (action?: string) => {
    if (!action) return 'gray'
    if (action.includes('CREATE')) return 'green'
    if (action.includes('UPDATE')) return 'blue'
    if (action.includes('DELETE')) return 'red'
    if (action.includes('LOGIN')) return 'purple'
    if (action.includes('VIEW')) return 'cyan'
    return 'gray'
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="auto" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <HStack justify="space-between" align="center">
                <Box>
                  <Heading size="md" mb={1}>
                    Activity Logs
                  </Heading>
                  <Text fontSize="sm" color={colors.text}>
                    Comprehensive system-wide audit trail of all administrative actions and system operations
                  </Text>
                </Box>
                <Button size="sm" leftIcon={<RefreshCw size={16} />} onClick={handleRefresh}>
                  Refresh
                </Button>
              </HStack>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <Tabs
                  index={activeTab === 'today' ? 0 : activeTab === 'range' ? 1 : 2}
                  onChange={handleTabChange}
                >
                  <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                    <TabList borderBottom="none">
                      <Tab>
                        Today's Logs{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      <Tab>Date Range</Tab>
                      <Tab>Advanced Search</Tab>
                    </TabList>
                  </HStack>
                  <Box borderBottom="2px" borderColor={colors.border} mx={4} />
                </Tabs>
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              {activeTab === 'today' && (
                <HStack spacing={2} justify="flex-start">
                  <Box flex="0 1 auto" minW="300px" maxW="400px">
                    <InputGroup size="sm">
                      <Input
                        ref={searchRef}
                        placeholder="Search audit logs..."
                        onKeyDown={handleKeyDown}
                        defaultValue={params.search}
                        pr="2.5rem"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          aria-label="Search"
                          icon={<Search size={16} />}
                          size="sm"
                          h="1.75rem"
                          w="1.75rem"
                          onClick={handleSearch}
                          variant="ghost"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                </HStack>
              )}

              {activeTab === 'range' && (
                <HStack spacing={4} align="flex-end" flexWrap="wrap">
                  <Box flex="0 1 auto" minW="200px">
                    <Input
                      type="date"
                      size="sm"
                      value={dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    />
                  </Box>
                  <Box flex="0 1 auto" minW="200px">
                    <Input
                      type="date"
                      size="sm"
                      value={dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    />
                  </Box>
                  <Box flex="0 1 auto" display="flex" alignItems="flex-end">
                    <Button size="sm" colorScheme="blue" onClick={handleApplyRange}>
                      Apply Range
                    </Button>
                  </Box>
                  <Box flex="0 1 auto" minW="300px" maxW="400px">
                    <InputGroup size="sm">
                      <Input
                        ref={searchRef}
                        placeholder="Search audit logs..."
                        onKeyDown={handleKeyDown}
                        defaultValue={params.search}
                        pr="2.5rem"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          aria-label="Search"
                          icon={<Search size={16} />}
                          size="sm"
                          h="1.75rem"
                          w="1.75rem"
                          onClick={handleSearch}
                          variant="ghost"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                </HStack>
              )}

              {activeTab === 'search' && (
                <HStack spacing={4} flexWrap="wrap" align="flex-end">
                  <Box flex="0 1 auto" minW="150px">
                    <Input
                      type="number"
                      size="sm"
                      placeholder="User ID"
                      value={searchFilters.user_id}
                      onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    />
                  </Box>
                  <Box flex="0 1 auto" minW="200px">
                    <Select
                      size="sm"
                      value={searchFilters.module}
                      onChange={(e) => handleFilterChange('module', e.target.value)}
                    >
                      <option value="">All Modules</option>
                      <option value="AUTHENTICATION">Authentication</option>
                      <option value="USER_MANAGEMENT">User Management</option>
                      <option value="CONTENT_MANAGEMENT">Content Management</option>
                      <option value="INVENTORY_MANAGEMENT">Inventory Management</option>
                      <option value="AUDIT_MANAGEMENT">Audit Management</option>
                      <option value="STORE_MANAGEMENT">Store Management</option>
                      <option value="SYSTEM_SETTINGS">System Settings</option>
                      <option value="DASHBOARD">Dashboard</option>
                      <option value="FILE_MANAGEMENT">File Management</option>
                    </Select>
                  </Box>
                  <Box flex="0 1 auto" minW="200px">
                    <Select
                      size="sm"
                      value={searchFilters.action}
                      onChange={(e) => handleFilterChange('action', e.target.value)}
                    >
                      <option value="">All Actions</option>
                      <option value="CREATE">Create</option>
                      <option value="UPDATE">Update</option>
                      <option value="DELETE">Delete</option>
                      <option value="VIEW">View</option>
                      <option value="LOGIN_SUCCESS">Login Success</option>
                      <option value="LOGIN_FAILED">Login Failed</option>
                      <option value="LOGOUT">Logout</option>
                      <option value="UPLOAD_FILE">Upload File</option>
                      <option value="DELETE_FILE">Delete File</option>
                      <option value="IMPORT">Import</option>
                      <option value="EXPORT">Export</option>
                    </Select>
                  </Box>
                  <Box flex="0 1 auto" minW="200px">
                    <Input
                      type="text"
                      size="sm"
                      placeholder="Search in data..."
                      value={searchFilters.keyword}
                      onChange={(e) => handleFilterChange('keyword', e.target.value)}
                    />
                  </Box>
                  <Box flex="0 1 auto" display="flex" alignItems="flex-end">
                    <Button size="sm" colorScheme="blue" onClick={handleApplySearch}>
                      Search
                    </Button>
                  </Box>
                </HStack>
              )}
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={showSubSubCallback}
                  customRenderers={{
                    timestamp: (value, row: AuditLog) =>
                      row.timestamp_readable || new Date(value).toLocaleString(),
                    module: (value) => (
                      <Badge colorScheme={getModuleColor(value)}>
                        {value || 'N/A'}
                      </Badge>
                    ),
                    action: (value) => (
                      <Badge colorScheme={getActionColor(value)}>
                        {value || 'N/A'}
                      </Badge>
                    ),
                    user_agent: (value) => (
                      <Text fontSize="xs" noOfLines={1} maxW="300px" title={value}>
                        {value || 'N/A'}
                      </Text>
                    ),
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

